import { WorkPage } from '../../components/work-page.tsx';
import '../portal-page.css';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { adminAffiliatesApi, AdminApiError, type AdminPayout, type AdminPayoutDetail } from '../../api/admin.ts';
import AdminElevateDialog, { isAdminFreshAuthRequired } from '../../components/admin/elevate-dialog.tsx';
import { Alert, Button, ConfirmDialog, LoadingBlock, Select } from '../../components/ui.tsx';
import { useDocumentTitle } from '../../hooks/index.ts';
import {
  Pill,
  TableShell,
  Td,
  Th,
  money,
  payoutStatusTone,
  periodLabel,
  recentPeriods,
  shortDate,
} from './affiliate-common.tsx';

/**
 * The monthly payout run, one batch per commission month:
 *
 *   calculate → approve → execute → reconcile
 *
 * Calculation is repeatable while the numbers are still a draft; approval
 * freezes them (and finalizes the period's ledger, which is what partners see
 * as "payable"); execution sends the money through PayPal and is the one
 * action in this console gated on a fresh second factor — the API answers
 * FRESH_AUTH_REQUIRED and the elevate dialog is the way through. A batch
 * PayPal has accepted is `processing` until reconciliation confirms what
 * actually settled: acceptance is not payment.
 */

export default function AdminPartnerPayoutsPage() {
  useDocumentTitle('Partner payouts');
  const queryClient = useQueryClient();

  // Only closed months are offered. The current month is still accruing, and
  // approval freezes a batch at whatever entries exist that moment — a payout
  // run early would strand every commission earned later in the same month,
  // with no way to join the frozen batch. The API refuses it too; this keeps
  // the impossible option out of the selector entirely. Two years deep, so a
  // failed or unreconciled batch stays reachable long after its month.
  const periods = recentPeriods(25).slice(1);
  const [period, setPeriod] = useState(periods[0] ?? '');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busyAction, setBusyAction] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<'approve' | 'execute' | null>(null);
  const [dialogError, setDialogError] = useState<string | null>(null);
  // The period whose execution the freshness gate interrupted. Held
  // explicitly rather than read back from the month selector on retry: the
  // selector stays usable while the elevation dialog is up, and the batch the
  // operator confirmed — its amount, its partner count — is the only one the
  // verified retry may send.
  const [pendingExecute, setPendingExecute] = useState<string | null>(null);
  // No factor enrolled at all — a different answer than "prove one now".
  // Without its own branch this fell through to a generic error, on a console
  // whose navigation offers no path to enrollment.
  const [mfaSetupNeeded, setMfaSetupNeeded] = useState(false);

  const overview = useQuery({
    queryKey: ['admin', 'affiliates', 'overview'],
    queryFn: ({ signal }) => adminAffiliatesApi.overview(signal),
  });
  const detail = useQuery({
    queryKey: ['admin', 'affiliates', 'payout', period],
    queryFn: ({ signal }) => adminAffiliatesApi.getPayout(period, signal),
    enabled: period.length > 0,
  });

  // React Query keeps the last successful data when a refetch fails. Payout
  // actions must use only a successful, settled read for the selected month.
  const detailReady = detail.isSuccess && !detail.isFetching;
  const payout = detailReady ? detail.data.payout : null;
  const payoutsConfigured =
    (detailReady ? detail.data.payoutsConfigured : undefined) ??
    (overview.isSuccess && !overview.isFetching ? overview.data.payoutsConfigured : undefined);

  useEffect(() => {
    if (!detailReady) {
      setConfirming(null);
      setDialogError(null);
    }
  }, [detailReady]);

  async function refresh() {
    await queryClient.invalidateQueries({ queryKey: ['admin', 'affiliates'] });
  }

  async function run(action: string, work: () => Promise<string | null>) {
    setBusyAction(action);
    setError(null);
    setNotice(null);
    setDialogError(null);
    try {
      const message = await work();
      setConfirming(null);
      if (message) setNotice(message);
      await refresh();
    } catch (caught) {
      const message = caught instanceof AdminApiError ? caught.message : 'That did not work. Please try again.';
      // Inside the open confirmation if one is up, on the page otherwise.
      if (confirming) setDialogError(message);
      else setError(message);
    } finally {
      setBusyAction(null);
    }
  }

  function calculate() {
    if (!detailReady) return;
    void run('calculate', async () => {
      await adminAffiliatesApi.calculatePayout(period);
      return `Payout for ${periodLabel(period)} calculated.`;
    });
  }

  function approve() {
    if (!detailReady || !payout || !['calculated', 'under_review'].includes(payout.status)) return;
    void run('approve', async () => {
      const result = await adminAffiliatesApi.approvePayout(period);
      return result.approved ? `Payout for ${periodLabel(period)} approved — estimates are now payable.` : null;
    });
  }

  function execute(target: string) {
    const targetState = queryClient.getQueryState<AdminPayoutDetail>(['admin', 'affiliates', 'payout', target]);
    if (
      targetState?.status !== 'success' ||
      targetState.fetchStatus !== 'idle' ||
      targetState.data?.payout?.status !== 'approved'
    ) {
      setConfirming(null);
      setPendingExecute(null);
      setError('Load the payout again before sending it.');
      return;
    }
    setBusyAction('execute');
    setDialogError(null);
    adminAffiliatesApi
      .executePayout(target)
      .then(async (result) => {
        setConfirming(null);
        setPendingExecute(null);
        // Two different bad answers, never blurred: a payout back in
        // `approved` was REFUSED — the service rolled the ledger back and
        // nothing was paid — while `processing` with a failureReason means
        // PayPal's answer was LOST (a timeout or 5xx after the hand-off) and
        // the batch may well have been accepted. Calling the second one "not
        // sent" invites the exact double-payment reconciliation exists to
        // prevent. Neither may read as a green result.
        if (result.failureReason) {
          setError(
            result.status === 'processing'
              ? `PayPal's answer for the ${periodLabel(target)} batch was lost (${result.failureReason}). It may or may not have been accepted — reconcile to learn the outcome; nothing further can be sent until then.`
              : `The ${periodLabel(target)} payout was not sent: ${result.failureReason}`,
          );
        } else {
          setNotice(
            `${periodLabel(target)} batch handed to PayPal (${result.sentCount} payment${result.sentCount === 1 ? '' : 's'}, batch ${result.batchId ?? 'n/a'}). Reconcile to confirm settlement.`,
          );
        }
        await refresh();
      })
      .catch((caught: unknown) => {
        if (isAdminFreshAuthRequired(caught)) {
          // The gate, not a failure: prove a factor and the retry goes
          // through — against `target`, whatever the selector shows by then.
          setConfirming(null);
          setPendingExecute(target);
          return;
        }
        if (caught instanceof AdminApiError && caught.code === 'MFA_SETUP_REQUIRED') {
          setConfirming(null);
          setMfaSetupNeeded(true);
          return;
        }
        const message = caught instanceof AdminApiError ? caught.message : 'The payout could not be executed.';
        if (confirming) setDialogError(message);
        else setError(message);
      })
      .finally(() => setBusyAction(null));
  }

  function reconcile() {
    if (!detailReady || payout?.status !== 'processing') return;
    void run('reconcile', async () => {
      const result = await adminAffiliatesApi.reconcilePayout(period);
      const status = result.payout?.status;
      return status === 'processing'
        ? 'PayPal is still settling this batch — check again later.'
        : `Reconciled: the batch is now ${status?.replace(/_/g, ' ') ?? 'unchanged'}.`;
    });
  }

  // A failed or still-running refetch leaves old data in the cache. It must
  // not offer a payout action or keep an earlier confirmation open.
  const canCalculate = detailReady && (!payout || ['open', 'calculated', 'under_review'].includes(payout.status));
  const canApprove = detailReady && payout ? ['calculated', 'under_review'].includes(payout.status) : false;
  const canExecute = detailReady && payout?.status === 'approved';
  const canReconcile = detailReady && payout?.status === 'processing';

  return (
    <div className="portal-page">
      <WorkPage
        title="Partner payouts"
        description="One batch per commission month. Calculate while it is a draft, approve to freeze the numbers, execute to send the money, reconcile to learn what settled."
      >
        <p className="mb-3 text-sm">
          <Link to="/admin/partners" className="text-[color:var(--color-brand)] hover:underline">
            ← All partners
          </Link>
        </p>

        {overview.isError ? (
          <div className="mb-4">
            <Alert tone="error">
              Program settings could not be loaded.{' '}
              <Button variant="ghost" onClick={() => void overview.refetch()}>
                Try again
              </Button>
            </Alert>
          </div>
        ) : null}
        {payoutsConfigured === false ? (
          <div className="mb-4">
            <Alert tone="warning" title="PayPal payouts are not configured">
              Calculation and approval work; execution will be refused until the PayPal credentials are set on this
              deployment.
            </Alert>
          </div>
        ) : null}
        {error ? (
          <div className="mb-4">
            <Alert tone="error">{error}</Alert>
          </div>
        ) : null}
        {notice ? (
          <div className="mb-4">
            <Alert tone="success">{notice}</Alert>
          </div>
        ) : null}

        {pendingExecute ? (
          <div className="mb-4">
            <AdminElevateDialog
              onVerified={() => {
                // The dialog closes BEFORE the retry starts. It re-enables its
                // own Confirm the moment this callback returns, so leaving it up
                // for the request's duration offers a second click that would
                // race the send — refused server-side, but reported back as a
                // failure over a payment that is in fact going out.
                const target = pendingExecute;
                setPendingExecute(null);
                if (target) execute(target);
              }}
              onCancel={() => setPendingExecute(null)}
            />
          </div>
        ) : null}

        {mfaSetupNeeded ? (
          <div className="mb-4">
            <Alert tone="warning" title="A second sign-in code is not set up">
              Sending a payout asks for a fresh second code, and this account has none set up.{' '}
              <Link to="/admin/security" className="font-medium underline">
                Set one up on the Security page
              </Link>
              , then come back to this batch — it stays approved and ready.
            </Alert>
          </div>
        ) : null}

        <div className="mb-4 flex flex-wrap items-end gap-3">
          <div className="w-56">
            <Select
              label="Commission month"
              options={periods.map((value) => ({ value, label: periodLabel(value) }))}
              value={period}
              onChange={(event) => setPeriod(event.target.value)}
            />
          </div>
          <div className="flex flex-wrap gap-2 pb-4">
            {canCalculate ? (
              <Button busy={busyAction === 'calculate'} onClick={calculate}>
                {payout ? 'Recalculate' : 'Calculate'}
              </Button>
            ) : null}
            {canApprove ? (
              <Button variant="primary" onClick={() => setConfirming('approve')}>
                Approve…
              </Button>
            ) : null}
            {canExecute ? (
              <Button variant="danger" disabled={!payoutsConfigured} onClick={() => setConfirming('execute')}>
                Execute payout…
              </Button>
            ) : null}
            {canReconcile ? (
              <Button busy={busyAction === 'reconcile'} onClick={reconcile}>
                Reconcile with PayPal
              </Button>
            ) : null}
          </div>
        </div>

        {!detailReady && !detail.isError ? (
          <LoadingBlock label="Loading payout…" />
        ) : detail.isError ? (
          <Alert tone="error" title="The payout could not be loaded">
            This is a fetch failure, not an empty month — nothing here says whether {periodLabel(period)} has a payout.{' '}
            <Button variant="ghost" onClick={() => void detail.refetch()}>
              Try again
            </Button>
          </Alert>
        ) : !payout ? (
          <p className="text-sm text-[color:var(--color-muted)]">
            No payout has been calculated for {periodLabel(period)} yet.
          </p>
        ) : (
          <>
            <PayoutSummaryCard payout={payout} minimumPayoutCents={detail.data?.minimumPayoutCents} />

            <section className="mt-6">
              <h2 className="mb-3 text-base font-semibold">Per-partner items</h2>
              {detail.data?.items.length === 0 ? (
                <p className="text-sm text-[color:var(--color-muted)]">
                  No items — nobody is owed anything this month.
                </p>
              ) : (
                <TableShell
                  label="Per-partner items"
                  head={
                    <tr>
                      <Th>Partner</Th>
                      <Th className="text-right">Amount</Th>
                      <Th>Status</Th>
                      <Th>Hold reason</Th>
                      <Th>PayPal</Th>
                      <Th className="text-right">Carried forward</Th>
                      <Th>Provider</Th>
                    </tr>
                  }
                >
                  {(detail.data?.items ?? []).map((item) => (
                    <tr key={item.id} className="border-b border-[color:var(--color-line)] last:border-b-0">
                      <Td>
                        <Link
                          to={`/admin/partners/${item.affiliateId}`}
                          className="font-medium text-[color:var(--color-brand)] hover:underline"
                        >
                          {item.affiliateId}
                        </Link>
                      </Td>
                      <Td className="text-right font-medium">{money(item.amountCents, item.currency)}</Td>
                      <Td>
                        <Pill value={item.status} tone={payoutStatusTone(item.status)} />
                      </Td>
                      <Td className="text-[color:var(--color-muted)]">{item.holdReason?.replace(/_/g, ' ') ?? '—'}</Td>
                      <Td>{item.paypalOnFile ? 'On file' : 'Missing'}</Td>
                      <Td className="text-right text-[color:var(--color-muted)]">
                        {item.carriedForwardCents > 0 ? money(item.carriedForwardCents, item.currency) : '—'}
                      </Td>
                      <Td className="text-[color:var(--color-muted)]">
                        {item.providerStatus ?? '—'}
                        {item.failureReason ? (
                          <span className="block text-xs text-[color:var(--color-danger)]">{item.failureReason}</span>
                        ) : null}
                      </Td>
                    </tr>
                  ))}
                </TableShell>
              )}
            </section>
          </>
        )}

        {confirming === 'approve' && payout ? (
          <ConfirmDialog
            title={`Approve the ${periodLabel(period)} payout?`}
            confirmLabel="Approve payout"
            cancelLabel="Not yet"
            busy={busyAction === 'approve'}
            error={dialogError}
            onConfirm={approve}
            onCancel={() => setConfirming(null)}
          >
            <p className="text-sm">
              {money(payout.totalCents, payout.currency)} to {payout.partnerCount} partner
              {payout.partnerCount === 1 ? '' : 's'}. Approval freezes these numbers and finalizes the month&rsquo;s
              estimates into payable commission — the figures every partner sees. Recalculating is no longer possible
              afterwards; corrections become explicit adjustments.
            </p>
          </ConfirmDialog>
        ) : null}

        {confirming === 'execute' && payout ? (
          <ConfirmDialog
            title={`Send ${money(payout.totalCents, payout.currency)} now?`}
            confirmLabel="Execute payout"
            cancelLabel="Not yet"
            confirmVariant="danger"
            busy={busyAction === 'execute'}
            error={dialogError}
            onConfirm={() => execute(period)}
            onCancel={() => setConfirming(null)}
          >
            <p className="text-sm">
              This hands PayPal a batch of {payout.partnerCount} payment{payout.partnerCount === 1 ? '' : 's'} totalling{' '}
              {money(payout.totalCents, payout.currency)} for {periodLabel(period)}, out of the company&rsquo;s own
              account, irreversibly. A fresh two-factor check may be asked for first.
            </p>
          </ConfirmDialog>
        ) : null}
      </WorkPage>
    </div>
  );
}

function PayoutSummaryCard({ payout, minimumPayoutCents }: { payout: AdminPayout; minimumPayoutCents?: number }) {
  const rows: Array<[string, string]> = [
    ['Gross attributable revenue', money(payout.grossRevenueCents, payout.currency)],
    ['Base commission', money(payout.baseCommissionCents, payout.currency)],
    ['Bonuses', money(payout.bonusCents, payout.currency)],
    ['Reversals', money(payout.reversalCents, payout.currency)],
    ['Adjustments', money(payout.adjustmentCents, payout.currency)],
    ['Carried in from earlier months', money(payout.carryoverInCents, payout.currency)],
    ['Carried forward (under minimum)', money(payout.carryoverOutCents, payout.currency)],
    ['Held', money(payout.heldCents, payout.currency)],
  ];

  return (
    <section className="card">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-base font-semibold">{periodLabel(payout.period)}</h2>
        <Pill value={payout.status} tone={payoutStatusTone(payout.status)} />
        <span className="ml-auto text-2xl font-bold">{money(payout.totalCents, payout.currency)}</span>
      </div>
      <p className="mt-1 text-sm text-[color:var(--color-muted)]">
        {payout.partnerCount} partner{payout.partnerCount === 1 ? '' : 's'}
        {minimumPayoutCents !== undefined ? ` · ${money(minimumPayoutCents, payout.currency)} minimum payout` : ''}
        {payout.providerBatchId ? ` · PayPal batch ${payout.providerBatchId}` : ''}
      </p>
      {/* Hidden once the batch is paid: the service keeps the old reason
          (COALESCE on transition), so after a successful retry this would
          otherwise present a settled batch under a stale warning. */}
      {payout.failureReason && payout.status !== 'paid' ? (
        <div className="mt-3">
          <Alert tone="warning" title="Last attempt reported">
            {payout.failureReason}
          </Alert>
        </div>
      ) : null}
      <dl className="mt-4 grid gap-x-8 gap-y-1 text-sm sm:grid-cols-2">
        {rows.map(([label, value]) => (
          <div key={label} className="flex justify-between gap-4">
            <dt className="text-[color:var(--color-muted)]">{label}</dt>
            <dd className="font-medium">{value}</dd>
          </div>
        ))}
      </dl>
      <p className="mt-4 text-xs text-[color:var(--color-muted)]">
        Calculated {shortDate(payout.calculatedAt)} · approved {shortDate(payout.approvedAt)} · executed{' '}
        {shortDate(payout.executedAt)} · completed {shortDate(payout.completedAt)}
      </p>
    </section>
  );
}
